import { Link } from "react-router-dom";
import MovieCard from "@/components/feature/MovieCard";
import { useFavorites } from "@/hooks/useFavorites";
import { movies } from "@/mocks/movies";
import { categories } from "@/mocks/categories";
import { useSeo } from "@/hooks/useSeo";

export default function Favorites() {
  const { favorites, toggleFavorite } = useFavorites();
  const saved = favorites
    .map((id) => movies.find((movie) => movie.id === id))
    .filter((movie): movie is (typeof movies)[number] => Boolean(movie));

  useSeo({
    title: "我的收藏 - 日韩伦理电影资料站",
    description: "查看你收藏的日韩伦理电影资料，收藏状态保存在本地浏览器，刷新页面后仍然保留。",
    keywords: "我的收藏,日韩伦理电影收藏,伦理电影片单",
  });

  return (
    <div className="bg-background-50">
      <section className="border-b border-background-200/70 bg-accent-950">
        <div className="mx-auto w-full max-w-[1280px] px-4 pb-9 pt-7 md:px-8 md:pb-11 md:pt-9">
          <nav aria-label="面包屑" className="mb-5 flex items-center gap-1.5 font-label text-[11px] text-foreground-500">
            <Link to="/" className="transition-colors hover:text-primary-300">首页</Link>
            <i className="ri-arrow-right-s-line text-[13px]"></i>
            <span className="text-primary-300">我的收藏</span>
          </nav>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="mb-2 flex items-center gap-2 font-label text-[11px] uppercase tracking-[0.28em] text-accent-200">
                <span className="h-px w-6 bg-accent-400/70"></span>
                My collection
              </p>
              <h1 className="font-heading text-2xl font-semibold text-foreground-950 md:text-[30px]">
                我的收藏
                <span className="ml-2 align-middle font-label text-[11px] font-normal tracking-widest text-foreground-500">
                  {saved.length} 部
                </span>
              </h1>
              <p className="mt-2 max-w-xl text-[13px] leading-relaxed text-foreground-500">
                收藏记录保存在当前浏览器本地，不会上传服务器。清除浏览器数据后收藏会一并消失。
              </p>
            </div>
            {saved.length > 0 && (
              <button
                type="button"
                onClick={() => saved.forEach((movie) => toggleFavorite(movie.id))}
                className="inline-flex w-fit cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300/60 px-3.5 py-2 font-label text-[11.5px] text-foreground-600 transition-colors hover:border-primary-500 hover:text-primary-200"
              >
                <i className="ri-delete-bin-6-line"></i>
                清空收藏
              </button>
            )}
          </div>
        </div>
      </section>

      <div className="mx-auto w-full max-w-[1280px] px-4 py-10 md:px-8 md:py-14">
        {saved.length > 0 ? (
          <div className="grid grid-cols-2 gap-x-3 gap-y-7 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            {saved.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
        ) : (
          <div className="rounded-lg border border-dashed border-background-300/70 bg-background-100/40 px-4 py-20 text-center">
            <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full border border-background-300/60 text-foreground-500">
              <i className="ri-bookmark-line text-2xl"></i>
            </span>
            <h2 className="mt-4 font-heading text-lg font-semibold text-foreground-900">收藏夹还是空的</h2>
            <p className="mt-2 text-[12.5px] text-foreground-500">
              在影片卡片或详情页点击书签图标，就能把想看的作品存到这里。
            </p>
            <div className="mt-5 flex flex-wrap justify-center gap-2">
              {categories.map((category) => (
                <Link
                  key={category.key}
                  to={`/category/${category.key}`}
                  className="rounded-md border border-background-300/70 px-3 py-1.5 text-[11.5px] text-foreground-600 transition-colors hover:border-primary-500 hover:text-primary-200"
                >
                  {category.label}电影
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}